import { onScopeDispose, shallowRef, toValue, type MaybeRefOrGetter } from "vue";
import { connectTileId } from "../config/tile";
import type { AgentRecordContext } from "./assistantChannel";

/** What a tile tells the workspace around it (I-3641). */
export interface ConnectTileMessage {
  type: "connect.tile";
  tileId: string;
  /** The tile's own code runs: it loaded, its app may still take a while. */
  booted?: boolean;
  /** It cannot show its page (a file did not load): it needs a fresh frame. */
  failed?: boolean;
  /** The user pressed a pointer or a key in it: no input in a frame reaches the page around it. */
  pressed?: boolean;
  /** The route the tile shows now; reopened from here after a reload. */
  path?: string;
  /** Caption of the tile's head: the open document or record. */
  title?: string;
  /** The area of its app the tile shows („Angebote“); names the browser tab along with the caption. */
  area?: string;
  /** The tile's app menu, for the one navbar at the top of the page (`shell.tiles.navbar`). */
  nav?: ConnectTileNav;
  /** Open this route beside the tile or in a new workspace (the table's `row_open`). */
  open?: {
    path: string;
    target: "split" | "new-split" | "workspace" | "tile";
    /** The tile to open in, for `tile`. */
    tileId?: string;
  };
}

/** A navbar entry as it can cross into the page around the tile. */
export interface ConnectTileNavItem {
  id: string;
  text: string;
  /** The icon as SVG markup: a component does not cross windows. */
  icon?: string;
  iconOnly?: boolean;
  disabled?: boolean;
  badge?: string | number | null;
  children?: ConnectTileNavItem[];
}

export interface ConnectTileNav {
  left: ConnectTileNavItem[];
  right: ConnectTileNavItem[];
}

/** From the page around the tile into it: run a menu entry, or the navbar setting changed. */
export type ConnectTileCommand =
  | { type: "connect.tile.nav-action"; itemId: string }
  | { type: "connect.tile.navbar"; mode: ConnectTileNavbarMode }
  | { type: "connect.tile.navigate"; path: string }
  | { type: "connect.tile.targets"; tiles: { id: string; label: string }[] }
  | { type: "connect.tile.assistant-context"; requestId: string };

/**
 * From a tile to the page around it: the record the tile shows, as answer to
 * `connect.tile.assistant-context`. The shell's assistant and voice button live
 * in the page, the record claimant (B-DMS) lives in the tile.
 */
export interface ConnectTileAssistantContextReply {
  type: "connect.tile.assistant-context.reply";
  tileId: string;
  requestId: string;
  context: AgentRecordContext | null;
}

/** `shared`: one navbar on top, showing the active tile's menu. `tile`: each tile has its own. */
export type ConnectTileNavbarMode = "shared" | "tile";

/** Tell the surrounding workspace about this tile; does nothing outside a tile. */
export function reportConnectTile(
  state: Pick<
    ConnectTileMessage,
    "booted" | "failed" | "pressed" | "path" | "title" | "area" | "nav" | "open"
  >,
): void {
  const tileId = connectTileId();
  if (!tileId || window.parent === window) return;
  const message: ConnectTileMessage = {
    type: "connect.tile",
    tileId,
    ...state,
  };
  window.parent.postMessage(message, window.location.origin);
}

type PageTitleSource = () => string | ConnectPageRecord | undefined;

/**
 * What the views on screen say they show, newest last: a view taking over may
 * set up before the one it replaces has gone.
 */
const pageTitles: PageTitleSource[] = [];
/**
 * The newest of them. A view going only writes it: unmounting runs inside the
 * parent's render effect, and a read there would make that effect depend on it.
 */
const pageTitle = shallowRef<PageTitleSource>();

/**
 * A record by its number and its name (say document number and matchcode); which
 * of them shows is the user's choice (`shell.browser_title.record`). A record
 * still loading has neither: anything but a text counts as missing.
 */
export interface ConnectPageRecord {
  number?: unknown;
  name?: unknown;
}

/**
 * Name the open record, or a document by a plain text: the caption of this tile's
 * head and the start of the browser tab's title, with or without tiles (I-3777).
 * It lasts as long as the calling scope, so a page left behind leaves no name behind.
 */
export function useConnectTileTitle(
  title: MaybeRefOrGetter<string | ConnectPageRecord | undefined>,
): void {
  const source = () => toValue(title);
  pageTitles.push(source);
  pageTitle.value = source;
  onScopeDispose(() => {
    pageTitles.splice(pageTitles.indexOf(source), 1);
    pageTitle.value = pageTitles.at(-1);
  });
}

/** The open record of this page, a document by its name; empty when no view names one. */
export function connectPageRecord(): { number: string; name: string } {
  const current = pageTitle.value?.();
  const text = (part: unknown) => (typeof part === "string" ? part.trim() : "");
  if (!current || typeof current === "string")
    return { number: "", name: text(current) };
  return { number: text(current.number), name: text(current.name) };
}

/**
 * What the page around a tile may ask it before closing it, called across the
 * same-origin frame. A probe through `beforeunload` is not side-effect free (an
 * app may tear itself down there) and does not see an in-app route guard.
 */
export interface ConnectTileFrame {
  /** Work that closing the tile would lose. */
  hasUnsavedWork(): boolean;
  /** Runs the app's route-leave guards; false when one of them kept the user here. */
  leave(): Promise<boolean>;
}

const unsavedChecks = new Set<() => boolean>();

/** What a view left behind when it went (`keepAs`), until a view of the same name takes over. */
const leftUnsaved = new Map<string, () => boolean>();

/**
 * Say what closing this tile would lose; a device cache that dies with the tile
 * counts. The check lives as long as the calling scope — but another app of the
 * tile can replace the view, and what it left unsaved sits in a cache slot that
 * nobody opens again once the tile is closed. `keepAs` names the view: after it
 * went, it stays reported until a view of that name registers again and answers
 * for itself. By default the answer at that moment is kept; `live` keeps asking
 * the check instead, for state that still settles after the view is gone (a
 * write to the cache that is under way).
 */
export function useConnectTileUnsaved(
  check: () => boolean,
  options: { keepAs?: string; live?: boolean } = {},
): void {
  if (!connectTileId()) return;
  const { keepAs, live } = options;
  unsavedChecks.add(check);
  if (keepAs) leftUnsaved.delete(keepAs);
  onScopeDispose(() => {
    unsavedChecks.delete(check);
    if (!keepAs) return;
    const left = check();
    leftUnsaved.set(keepAs, live ? check : () => left);
  });
}

export function connectTileHasUnsavedWork(): boolean {
  return (
    [...unsavedChecks].some((check) => check()) ||
    [...leftUnsaved.values()].some((check) => check())
  );
}

/** Tests: forget what views left behind. */
export function resetConnectTileUnsaved(): void {
  unsavedChecks.clear();
  leftUnsaved.clear();
}

const FRAME_FIELD = "__connectTileFrame";

export function exposeConnectTileFrame(frame: ConnectTileFrame): void {
  (window as unknown as Record<string, unknown>)[FRAME_FIELD] = frame;
}

/** Null until the frame has started: nothing in it can be lost yet. */
export function connectTileFrameOf(
  target: Window | null | undefined,
): ConnectTileFrame | null {
  if (!target) return null;
  try {
    return (
      ((target as unknown as Record<string, unknown>)[
        FRAME_FIELD
      ] as ConnectTileFrame) ?? null
    );
  } catch {
    // A browser error page in the frame is another origin: nothing to ask, nothing to lose.
    return null;
  }
}
